import { Link } from '../routing';
import { formatCurrency } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';
import { changeTone, formatChangePct, rowChangePct, priceRangeLine } from '../utils/topProducts';
import FilterSheet from './FilterSheet';
import { useProductOrders, OrdersState, qtyText } from './TopProductOrders';

// One product of המוצרים המובילים ברכש on a PHONE, opened from a row of the
// report: its current price and the orders behind it, each one link away from
// the document itself. Wider screens open the same orders as sub-lines under
// the row instead (TopProductOrders.jsx, owner 22/10/2026).
//
// TRIMMED TO WHAT A NEGOTIATION USES (owner + product-manager review,
// 21/10/2026): name, then supplier · מק״ט on one quiet line; the current price
// and its % chip; one "מינ׳ · מקס׳" line; then the orders, which are the
// evidence. Dropped as repeating the chip or the orders: the min–max bar, the
// first price (the oldest order IS it), and the "highest in the period" note.
const TONE_CLASS = { up: 'is-up', down: 'is-down', flat: 'is-zero' };

export function PriceChangeChip({ pct }) {
  const text = formatChangePct(pct);
  if (!text) return null;
  return <span className={`m-delta ${TONE_CLASS[changeTone(pct)]}`}>{text}</span>;
}

// A row's change vs the first purchase in the period: a grey 0% for a flat
// price, nothing for a single purchase (rowChangePct).
export function RowChangeChip({ row }) {
  return <PriceChangeChip pct={rowChangePct(row)} />;
}

const money = (n) => (n == null ? '—' : formatCurrency(n));

const linkStyle = { color: 'var(--link)', textDecoration: 'underline', textUnderlineOffset: 2 };

function DocLink({ order, onClose }) {
  return (
    <Link to={`/document/${order.doc_id}`} onClick={onClose} style={linkStyle}>
      {order.doc_num || `#${order.doc_id}`}
    </Link>
  );
}

function CurrentPrice({ product }) {
  return (
    <span className="tps-price">
      <b>{money(product.current_price)}</b>
      <RowChangeChip row={product} />
    </span>
  );
}

// "מינ׳ ₪… · מקס׳ ₪…", or the unit-mismatch warning, or nothing.
function RangeLine({ product }) {
  const line = priceRangeLine(product);
  if (!line) return null;
  if (line.kind === 'units') {
    return <div className="tps-range tps-range-warn">פער חריג בין המחירים — ייתכן שמדובר ביחידות שונות</div>;
  }
  return <div className="tps-range">מינ׳ {formatCurrency(line.min)} · מקס׳ {formatCurrency(line.max)}</div>;
}

const ordersTitle = (orders) => `הזמנות${orders ? ` (${orders.length})` : ''}`;
const subtitle = (product) => [product.supplier_name, product.sku && `מק״ט ${product.sku}`].filter(Boolean).join(' · ');

function PhoneSheet({ product, orders, error, onClose }) {
  return (
    <FilterSheet open onClose={onClose} title={product.name}>
      <div className="tps-sub">{subtitle(product)}</div>
      <div className="tps-price-row">
        <span className="tps-label">מחיר נוכחי</span>
        <CurrentPrice product={product} />
      </div>
      <RangeLine product={product} />
      <div className="tps-orders-head">
        <div className="tps-orders-title">{ordersTitle(orders)}</div>
        {/* The period's totals sit with the orders they sum. */}
        <div className="tps-muted tps-num">סה״כ {formatCurrency(product.spend)} ללא מע״מ · כמות {qtyText(product.qty)}</div>
      </div>
      <OrdersState orders={orders} error={error} />
      {orders?.length > 0 && (
        <div className="m-list">
          {orders.map(o => (
            <div key={o.doc_id} className="m-subrow">
              <div className="m-subrow-main">
                <div className="m-subrow-primary">{formatDateShort(o.date)} · <DocLink order={o} onClose={onClose} /></div>
                <div className="m-subrow-meta">
                  {o.doc_type} · {qtyText(o.qty)}{o.unit_price != null && <> × {formatCurrency(o.unit_price)}</>}
                </div>
              </div>
              <span className="m-subrow-amount">{formatCurrency(o.spend)}</span>
            </div>
          ))}
        </div>
      )}
    </FilterSheet>
  );
}

export default function TopProductSheet({ product, params, onClose }) {
  const { orders, error } = useProductOrders(product, params);
  return <PhoneSheet product={product} orders={orders} error={error} onClose={onClose} />;
}
